import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Quote } from 'lucide-react';
import Icon from './DashboardIcons';
import PeriodRangePicker from '../ui/PeriodRangePicker';
import { getRoleLabel } from '../../config/organization';

const getGreeting = () => {
  const hour = new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Riyadh' })).getHours();
  return hour >= 5 && hour < 18 ? 'صباح الخير' : 'مساء الخير';
};

const MOTIVATIONAL_QUOTES = [
  'كل خطوة منظمة اليوم تصنع نجاحًا أكبر لمنشأتك غدًا.',
  'الأرقام تخبرك بما حدث، والتنظيم يساعدك على صناعة القادم.',
  'فريق ناجح يبدأ من إدارة تعرف أين تقف وإلى أين تتجه.',
  'عملك اليوم هو أساس إنجازات منشأتك القادمة.',
  'الإدارة الجيدة تحوّل الجهد المبذول إلى نتائج واضحة.',
  'الانضباط في التفاصيل الصغيرة يصنع الفارق في البطولات الكبيرة.',
  'كل لاعب تتابعه بعناية اليوم هو بطل محتمل في الغد.',
  'القرار الصحيح في الوقت المناسب يوفّر عليك أضعافه من الجهد لاحقًا.',
  'منشأة منظمة تستقطب الثقة قبل أن تستقطب النتائج.',
  'التميز ليس حدثًا عابرًا، بل نتيجة عادات إدارية يومية.',
  'خطوة واحدة نحو التنظيم توفّر عشر خطوات من التصحيح لاحقًا.',
  'أفضل الأندية لا تنتظر الفرصة، بل تُعدّ لها من الآن.',
  'إدارتك اليوم هي مباراة لا تُرى، لكنها تحسم نتائج الموسم.',
  'وضوح الأهداف يصنع فرقًا أقوى من وفرة الموارد.',
  'المتابعة اليومية الدقيقة هي سرّ الاستقرار طويل المدى.',
  'كل تقرير تراجعه بعناية هو قرار أفضل تتخذه لاحقًا.',
  'الثقة بين الإدارة واللاعبين تُبنى من الشفافية اليومية.',
  'النجاح المؤسسي يبدأ من نظام، لا من حظ.',
  'حين تُدار التفاصيل بحرفية، تُثمر النتائج من تلقاء نفسها.',
  'قيادة اليوم الهادئة تصنع بطولة الغد الصاخبة.',
  'الاستثمار في تنظيم الصفوف الإدارية يسبق الاستثمار في الملعب.',
  'كل موظف يشعر بالتقدير يقدّم أداءً يفوق التوقعات.',
  'التخطيط الجيد يجعل الأزمات فرصًا لا عقبات.',
  'منشأتك تنمو بقدر ما تنمو دقة قراراتك اليومية.',
  'العمل الجماعي المنظم يهزم الفردية الموهوبة دون نظام.',
  'كل بيانات دقيقة تجمعها اليوم توجّه قرارًا أذكى غدًا.',
  'الاحترافية تبدأ من التفاصيل التي لا يراها الجميع.',
  'إدارة حاضرة بالمتابعة تصنع فريقًا حاضرًا بالإنجاز.',
  'الاستمرارية في الأداء أهم من التميز في لحظة واحدة.',
  'كل يوم إداري منظم هو استثمار في سمعة منشأتك.',
];

const dayIndex = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), 0, 0);
  const diff = now - start;
  const oneDay = 1000 * 60 * 60 * 24;
  return Math.floor(diff / oneDay);
};

const PLAN_LABELS = { free: 'الباقة المجانية', basic: 'الباقة الأساسية', premium: 'الباقة المميزة', enterprise: 'باقة الشركات' };
const STATUS_LABELS = { trial: 'تجريبية', active: 'نشطة', expired: 'منتهية', cancelled: 'ملغاة', pending: 'قيد المراجعة' };

const useNow = () => {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  return now;
};

const DatePlanPanel = ({ club, trialStatus }) => {
  const now = useNow();
  const dayName = new Intl.DateTimeFormat('ar-SA', { weekday: 'long', timeZone: 'Asia/Riyadh' }).format(now);
  const gregorian = new Intl.DateTimeFormat('ar-SA-u-ca-gregory', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Asia/Riyadh' }).format(now);
  const hijri = new Intl.DateTimeFormat('ar-SA-u-ca-islamic-umalqura', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Asia/Riyadh' }).format(now);
  const time = new Intl.DateTimeFormat('ar-SA', { hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true, timeZone: 'Asia/Riyadh' }).format(now);

  const planLabel = club?.subscription_plan ? PLAN_LABELS[club.subscription_plan] : null;
  const statusLabel = trialStatus?.applicable
    ? (trialStatus.is_expired ? STATUS_LABELS.expired : STATUS_LABELS.trial)
    : (club?.subscription_status ? STATUS_LABELS[club.subscription_status] : null);
  const isHealthy = !(trialStatus?.applicable && !trialStatus.is_expired) && (club?.subscription_status ? club.subscription_status !== 'expired' && club.subscription_status !== 'cancelled' : true);

  let renewalLine = null;
  if (trialStatus?.applicable && !trialStatus.is_expired) {
    renewalLine = `متبقي ${trialStatus.days_remaining} ${trialStatus.days_remaining === 1 ? 'يوم' : 'أيام'} على انتهاء التجربة`;
  } else if (!trialStatus?.applicable && club?.subscription_end_date) {
    const end = new Date(club.subscription_end_date);
    if (!isNaN(end.getTime())) {
      renewalLine = `التجديد في ${new Intl.DateTimeFormat('ar-SA-u-ca-gregory', { day: 'numeric', month: 'long' }).format(end)}`;
    }
  }

  return (
    <div className="relative flex h-full flex-col justify-between gap-3 overflow-hidden rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3.5 text-white shadow-inner backdrop-blur-xl" data-testid="dashboard-date-plan-panel">
      <span className="absolute inset-x-0 top-0 h-[2px] bg-gradient-to-r from-orange-500 via-teal-400 to-transparent" />
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-base font-bold tracking-wide break-words" data-testid="dashboard-day-name">{dayName}</div>
          <div className="text-xs font-medium text-slate-200 break-words" data-testid="dashboard-gregorian-date">{gregorian}</div>
          <div className="text-[11px] font-normal text-teal-200/80 break-words" data-testid="dashboard-hijri-date">{hijri}</div>
        </div>
        <Icon name="calendar" className="h-4 w-4 shrink-0 text-teal-300/70" />
      </div>
      <div className="flex items-center gap-2 border-t border-white/10 pt-2.5">
        <span className="relative flex h-2 w-2 shrink-0">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-teal-400 opacity-75" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-teal-400 shadow-[0_0_8px_#14b8a6]" />
        </span>
        <div className="flex items-center gap-1.5 truncate font-mono text-lg font-bold tracking-wider text-teal-300" dir="ltr" data-testid="dashboard-live-clock">
          {time}
        </div>
      </div>
      {planLabel && (
        <div className="border-t border-white/10 pt-2.5">
          <div className="flex items-center justify-between gap-2">
            <Link
              to="/subscription"
              className="group flex items-center gap-1 truncate rounded-full bg-white/10 px-2.5 py-0.5 text-xs font-semibold transition-colors hover:bg-white/20"
              title="عرض باقات الاشتراك"
              data-testid="subscription-plan-badge"
            >
              <Icon name="star" className="h-3 w-3 shrink-0 text-orange-400" /> {planLabel}
              <span className="text-[10px] text-teal-300 opacity-70 transition-opacity group-hover:opacity-100">‹</span>
            </Link>
            {statusLabel && (
              <span className={`flex shrink-0 items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${isHealthy ? 'bg-teal-500/20 text-teal-300' : 'bg-orange-500/20 text-orange-300'}`} data-testid="subscription-status-badge">
                <span className={`h-1.5 w-1.5 rounded-full ${isHealthy ? 'bg-teal-400' : 'bg-orange-400'}`} /> {statusLabel}
              </span>
            )}
          </div>
          {renewalLine && <div className="mt-1.5 break-words text-[11px] text-slate-300" data-testid="subscription-renewal-info">{renewalLine}</div>}
        </div>
      )}
    </div>
  );
};

export const DashboardHeader = ({ user, club, trialStatus, periodRange, onPeriodChange }) => {
  const firstName = user?.full_name ? user.full_name.trim().split(/\s+/)[0] : '';
  const displayName = firstName || getRoleLabel(user?.role);
  const quote = MOTIVATIONAL_QUOTES[dayIndex() % MOTIVATIONAL_QUOTES.length];

  return (
    <div className="relative flex flex-col gap-5 overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-[#16233b] via-[#131f35] to-[#0d1626] p-5 shadow-xl shadow-black/20 sm:flex-row sm:items-stretch sm:p-6" data-testid="dashboard-header">
      <div aria-hidden="true" className="pointer-events-none absolute -left-16 -top-24 h-56 w-56 rounded-full bg-teal-500/15 blur-3xl" />
      <div aria-hidden="true" className="pointer-events-none absolute -bottom-24 -right-10 h-56 w-56 rounded-full bg-orange-500/10 blur-3xl" />

      <div className="relative z-10 min-w-0 flex-1 break-words">
        <span className="mb-2 inline-flex items-center gap-1.5 rounded-full bg-white/5 px-2.5 py-1 text-[11px] font-semibold text-teal-300">
          <span className="h-1.5 w-1.5 rounded-full bg-teal-400 animate-pulse" /> مباشر الآن
        </span>
        <h2 className="text-2xl font-extrabold leading-snug tracking-tight text-white break-words sm:text-3xl" data-testid="dashboard-greeting">
          {getGreeting()}{displayName ? `، ${displayName}` : ''}
        </h2>
        <p className="mt-1.5 text-sm font-medium leading-relaxed text-slate-300 break-words sm:text-base">
          إليك ملخص أعمال <span className="font-semibold text-teal-200">{club?.name || club?.club_name || 'منشأتك'}</span> اليوم
        </p>

        <div className="mt-4 flex max-w-md items-start gap-2.5 rounded-xl border border-teal-400/20 bg-gradient-to-r from-teal-500/15 via-emerald-500/10 to-transparent p-3 backdrop-blur-sm" data-testid="dashboard-motivational-quote">
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-teal-400/20 text-teal-300">
            <Quote className="h-3.5 w-3.5" />
          </span>
          <p className="text-xs font-medium leading-relaxed text-teal-100 break-words sm:text-sm">
            {quote}
          </p>
        </div>

        {periodRange && onPeriodChange && (
          <div className="mt-4 flex items-center gap-2">
            <label className="shrink-0 text-xs font-semibold text-slate-300">الفترة المعروضة</label>
            <PeriodRangePicker value={periodRange} onChange={onPeriodChange} theme="dark" testId="dashboard-period-filter" />
          </div>
        )}
      </div>
      <div className="relative z-10 shrink-0 sm:w-[260px]">
        <DatePlanPanel club={club} trialStatus={trialStatus} />
      </div>
    </div>
  );
};

export default DashboardHeader;

